import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AiFillGithub,
  AiOutlineTwitter,
  AiFillInstagram,
  AiFillHeart,
  AiOutlineHome,
  AiOutlineFundProjectionScreen,
  AiOutlineAppstore,
  AiOutlineTool,
  AiOutlineMail,
  AiOutlinePhone,
} from "react-icons/ai";
import { FaLinkedinIn, FaArrowUp, FaCode } from "react-icons/fa";
import { staggerContainer, fadeInUp } from "../utils/motion";
import { useScrollReveal } from "../hooks/useScrollReveal";

// Memoize Footer to prevent unnecessary re-renders
const Footer = React.memo(function Footer() {
  const date = new Date();
  const year = date.getFullYear();
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  useEffect(() => {
    let ticking = false;
    let rafId = null;
    
    const handleScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          setShowScrollTop(window.scrollY > 300);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const socialIcons = [
    { Icon: FaLinkedinIn, href: "https://linkedin.com", label: "LinkedIn", color: "#0077b5" },
    { Icon: AiFillInstagram, href: "https://instagram.com", label: "Instagram", color: "#E4405F" },
    { Icon: AiFillGithub, href: "https://github.com", label: "GitHub", color: "#ffffff" },
    { Icon: AiOutlineTwitter, href: "https://twitter.com", label: "Twitter", color: "#1DA1F2" },
  ];

  const pageLinks = [
    { path: "/", label: "Home", Icon: AiOutlineHome },
    { path: "/services", label: "Services", Icon: AiOutlineAppstore },
    { path: "/work", label: "Work", Icon: AiOutlineFundProjectionScreen },
    { path: "/process", label: "Process", Icon: AiOutlineTool },
    { path: "/contact", label: "Contact", Icon: AiOutlineMail },
  ];
  
  return (
    <motion.footer
      ref={ref}
      className="footer-wrapper"
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
    >
      {/* Simple Background Elements - CSS only, no JS animations */}
      <div className="footer-gradient-orb" />
      <div className="footer-gradient-orb footer-gradient-orb-secondary" />
      
      {/* Static Grid Pattern */}
      <div className="footer-grid-pattern" />
      
      {/* Scroll to Top Button */}
      <motion.button
        className="footer-scroll-top"
        onClick={scrollToTop}
        initial={{ opacity: 0, y: 30, scale: 0.8 }}
        animate={{ 
          opacity: showScrollTop ? 1 : 0,
          y: showScrollTop ? 0 : 30,
          scale: showScrollTop ? 1 : 0.8,
          pointerEvents: showScrollTop ? 'auto' : 'none'
        }}
        transition={{ 
          duration: 0.4, 
          ease: [0.22, 1, 0.36, 1],
          opacity: { duration: 0.3 }
        }}
        aria-label="Scroll to the top of the page"
        whileHover={{ 
          scale: 1.15, 
          y: -6,
          transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] }
        }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ 
            duration: 2.5, 
            repeat: Infinity, 
            ease: [0.4, 0, 0.6, 1],
            repeatDelay: 0.5
          }}
        >
          <FaArrowUp />
        </motion.div>
      </motion.button>
      
      <Container fluid className="footer">
        <motion.div
          className="footer-content"
          variants={staggerContainer}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          <Row className="footer-row">
            {/* Brand Section */}
            <Col lg={4} md={6} className="footer-brand">
              <motion.div variants={fadeInUp} className="footer-brand-content">
                {/* Logo */}
                <motion.div 
                  className="footer-logo-wrapper"
                  variants={fadeInUp}
                  whileHover={{ 
                    scale: 1.08,
                    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
                  }}
                >
                  <motion.div 
                    className="footer-logo"
                  >
                    <FaCode />
                  </motion.div>
                </motion.div>
                
                <motion.h2 
                  variants={fadeInUp}
                  className="footer-name"
                >
                  Mohit Portfolio
                </motion.h2>
                
                <motion.p 
                  variants={fadeInUp}
                  className="footer-tagline"
                >
                  Crafting digital experiences with code & creativity
                </motion.p>
                
                {/* Social Links */}
                <motion.div 
                  variants={fadeInUp}
                  className="footer-social-links"
                >
                  {socialIcons.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-social-link"
                      aria-label={social.label}
                      custom={index}
                      variants={fadeInUp}
                      whileHover={{ 
                        scale: 1.2,
                        y: -5,
                        transition: { 
                          duration: 0.3,
                          ease: [0.22, 1, 0.36, 1]
                        }
                      }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ 
                        duration: 0.25,
                        ease: [0.22, 1, 0.36, 1]
                      }}
                      style={{ '--social-color': social.color }}
                    >
                      <social.Icon />
                      <motion.span 
                        className="footer-social-link-shine"
                        initial={{ x: '-100%' }}
                        whileHover={{ 
                          x: '100%',
                          transition: { duration: 0.6, ease: "easeInOut" }
                        }}
                      />
                    </motion.a>
                  ))}
                </motion.div>
              </motion.div>
            </Col>
            
            {/* Quick Links Section */}
            <Col lg={2} md={6} className="footer-links">
              <motion.div variants={fadeInUp} className="footer-links-content">
                <motion.h3 
                  variants={fadeInUp}
                  className="footer-section-title"
                >
                  Quick Links
                </motion.h3>
                <motion.ul 
                  variants={staggerContainer}
                  className="footer-links-list"
                >
                  {pageLinks.map((link, index) => (
                    <motion.li
                      key={index}
                      variants={fadeInUp}
                      custom={index}
                    >
                      <Link 
                        to={link.path}
                        className="footer-link"
                      >
                        <motion.span
                          whileHover={{ 
                            x: 6,
                            transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
                          }}
                        >
                          {link.label}
                        </motion.span>
                        <motion.div 
                          className="footer-link-underline"
                          initial={{ scaleX: 0, opacity: 0 }}
                          whileHover={{ 
                            scaleX: 1, 
                            opacity: 1,
                            transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                          }}
                        />
                        <motion.div 
                          className="footer-link-bg"
                          initial={{ scaleX: 0, opacity: 0 }}
                          whileHover={{ 
                            scaleX: 1, 
                            opacity: 1,
                            transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
                          }}
                        />
                      </Link>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            </Col>
            
            {/* Info Section */}
            <Col lg={3} md={6} className="footer-info">
              <motion.div variants={fadeInUp} className="footer-info-content">
                <motion.h3 
                  variants={fadeInUp}
                  className="footer-section-title"
                >
                  Get In Touch
                </motion.h3>
                <motion.p 
                  variants={fadeInUp}
                  className="footer-info-text"
                >
                  Ready to bring your ideas to life? Let's collaborate and create something amazing together.
                </motion.p>
                <motion.div 
                  variants={fadeInUp}
                  className="footer-contact-info"
                  style={{ marginBottom: "1.5rem" }}
                >
                  <motion.a
                    href="mailto:mmkumar581@gmail.com"
                    className="footer-contact-link"
                    whileHover={{ 
                      x: 6,
                      transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
                    }}
                  >
                    <AiOutlineMail style={{ fontSize: "1.2rem", color: "#c770f0" }} />
                    <span>mmkumar581@gmail.com</span>
                  </motion.a>
                  <motion.a
                    href="tel:+919811766762"
                    className="footer-contact-link"
                    whileHover={{ 
                      x: 6,
                      transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
                    }}
                  >
                    <AiOutlinePhone style={{ fontSize: "1.2rem", color: "#c770f0" }} />
                    <span>+91 9811766762</span>
                  </motion.a>
                </motion.div>
                <motion.div 
                  variants={fadeInUp}
                  className="footer-cta"
                >
                  <Link to="/contact" className="footer-cta-button">
                    <motion.span
                      whileHover={{ 
                        x: 6,
                        transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
                      }}
                    >
                      Start a Project
                    </motion.span>
                    <motion.div 
                      className="footer-cta-shine"
                      initial={{ scaleX: 0, opacity: 0 }}
                      whileHover={{ 
                        scaleX: 1, 
                        opacity: 1,
                        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                      }}
                    />
                  </Link>
                </motion.div>
              </motion.div>
            </Col>
            
            {/* Bottom Bar */}
            <Col xs={12} className="footer-bottom">
              <motion.div 
                variants={fadeInUp}
                className="footer-bottom-content"
              >
                <motion.div 
                  className="footer-bottom-line"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ 
                    duration: 1, 
                    delay: 0.6,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                />
                <Row className="footer-bottom-row">
                  <Col md={6} className="footer-copyright-col">
                    <motion.p 
                      variants={fadeInUp}
                      className="footer-copyright"
                    >
                      Copyright © {year} Mohit Kumar. All rights reserved.
                    </motion.p>
                  </Col>
                  <Col md={6} className="footer-designed-col">
                    <motion.p 
                      variants={fadeInUp}
                      className="footer-designed"
                    >
                      Designed and Developed with{" "}
                      <motion.span
                        animate={{ 
                          scale: [1, 1.2, 1]
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity, 
                          ease: [0.4, 0, 0.6, 1],
                          repeatDelay: 0.5
                        }}
                      >
                        <AiFillHeart className="footer-heart" />
                      </motion.span>
                    </motion.p>
                  </Col>
                </Row>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </motion.footer>
  );
});

export default Footer;
